// src/lib/theme.js
export const PALETTE = {
    green: '#35FF45',
    greenLight: '#16A34A',
    blue: '#3160F4',
    blue2: '#3533CD',
    purple: '#A855F7',
    orange: '#F97316',
    gray: '#D0D0D4',
    white: '#FFFFFF',
    black: '#000000',
    font: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Helvetica Neue', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'"
};

export const THEME_CLASSES = {
    // STANDARD UI SIZING
    formHeight: 'h-[42px]',
    formRadius: '!rounded-xl', // Enforcing absolute consistency

    bgMain: (isDark) => isDark ? 'bg-black' : 'bg-[#F5F5F7]',
    bgCard: (isDark) => isDark ? 'bg-[#1c1c1e]' : 'bg-white',
    bgHighlight: (isDark) => isDark ? 'bg-white/5' : 'bg-slate-50',
    bgHover: (isDark) => isDark ? 'hover:bg-white/10' : 'hover:bg-slate-100',
    bgDrawer: (isDark) => isDark ? 'bg-[#1c1c1e]/95' : 'bg-white/95',

    textPrimary: (isDark) => isDark ? 'text-white' : 'text-black',
    textSecondary: (isDark) => isDark ? 'text-[#D0D0D4]/60' : 'text-slate-500',
    borderSubtle: (isDark) => isDark ? 'border-white/10' : 'border-[#D0D0D4]/30',

    input: (isDark) => isDark
        ? 'w-full h-[42px] bg-black/40 border border-white/10 rounded-xl px-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#3160F4]/50 placeholder:text-[#D0D0D4]/20'
        : 'w-full h-[42px] bg-white border border-[#D0D0D4] rounded-xl px-4 text-sm text-black focus:outline-none focus:ring-2 focus:ring-[#3160F4]/20 placeholder:text-slate-400',

    // --- NEW UNIFIED FORM ELEMENTS ---
    uniformInput: (isDark) => isDark
        ? 'w-full h-[42px] bg-black/40 border border-white/10 !rounded-xl px-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#3160F4]/50 placeholder:text-[#D0D0D4]/20'
        : 'w-full h-[42px] bg-white border border-[#D0D0D4] !rounded-xl px-4 text-sm text-black focus:outline-none focus:ring-2 focus:ring-[#3160F4]/20 placeholder:text-slate-400',

    colorPicker: (isDark) => `h-[42px] w-[42px] !rounded-xl cursor-pointer shrink-0 p-0 overflow-hidden transition-all active:scale-95 border-2 shadow-sm hover:shadow-md ${isDark ? 'border-white/20 bg-[#2c2c2e]' : 'border-[#D0D0D4] bg-slate-50'}`,

    buttonGroup: (isDark) => `p-1 !rounded-xl flex gap-1 border h-[42px] ${isDark ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-[#D0D0D4]/30'}`,

    buttonGroupItem: (isDark, isActive) => {
        const base = "flex-1 !rounded-lg text-xs font-bold transition-all h-full flex items-center justify-center";
        if (isActive) return `${base} text-white shadow-md`;
        return `${base} ${isDark ? 'text-[#D0D0D4]/60 hover:bg-white/10' : 'text-slate-500 hover:bg-slate-100'}`;
    },
    // ---------------------------------

    textGreen: (isDark) => isDark ? 'text-[#35FF45]' : 'text-[#16A34A]',
    bgGreen: (isDark) => isDark ? 'bg-[#35FF45] text-black' : 'bg-[#16A34A] text-white',
    bgGreenSoft: (isDark) => isDark ? 'bg-[#35FF45]/20' : 'bg-[#16A34A]/10',
    borderGreenSoft: (isDark) => isDark ? 'border-[#35FF45]/30' : 'border-[#16A34A]/20',

    bgBlueSoft: (isDark) => isDark ? 'bg-[#3160F4]/20' : 'bg-[#3160F4]/10',
    borderBlueSoft: (isDark) => isDark ? 'border-[#3160F4]/30' : 'border-[#3160F4]/20',

    activeBlueContainer: (isDark) => isDark
        ? 'border-2 border-[#3160F4]/60 shadow-[0_0_15px_-3px_rgba(49,96,244,0.2)]'
        : 'border-2 border-[#3160F4]/50 shadow-md',

    activeBlueHeader: (isDark) => isDark
        ? 'bg-[#3160F4]/30 border-b border-[#3160F4]/40'
        : 'bg-[#3160F4]/15 border-b border-[#3160F4]/30',

    activeGreenContainer: (isDark) => isDark
        ? 'border-2 border-[#35FF45]/50 shadow-[0_0_15px_-3px_rgba(53,255,69,0.15)]'
        : 'border-2 border-[#16A34A]/50 shadow-md',

    activeGreenHeader: (isDark) => isDark
        ? 'bg-[#35FF45]/20 border-b border-[#35FF45]/30'
        : 'bg-[#16A34A]/15 border-b border-[#16A34A]/30',

    activePurpleContainer: (isDark) => isDark
        ? 'border-2 border-[#A855F7]/60 shadow-[0_0_15px_-3px_rgba(168,85,247,0.2)]'
        : 'border-2 border-[#A855F7]/50 shadow-md',

    activePurpleHeader: (isDark) => isDark
        ? 'bg-[#A855F7]/30 border-b border-[#A855F7]/40'
        : 'bg-[#A855F7]/15 border-b border-[#A855F7]/30',

    activeOrangeContainer: (isDark) => isDark
        ? 'border-2 border-[#F97316]/60 shadow-[0_0_15px_-3px_rgba(249,115,22,0.2)]'
        : 'border-2 border-[#F97316]/50 shadow-md',

    activeOrangeHeader: (isDark) => isDark
        ? 'bg-[#F97316]/30 border-b border-[#F97316]/40'
        : 'bg-[#F97316]/15 border-b border-[#F97316]/30',

    // --- DESTRUCTIVE / DELETE STATES ---
    deleteOverlay: 'bg-rose-500/95 backdrop-blur-md',
    deleteBtnConfirm: 'bg-white text-rose-600 hover:bg-rose-50',
    deleteBtnCancel: 'bg-rose-900/40 text-white hover:bg-rose-900/60',
};